/**
 * Text for the Event Detail page's Basic Information fields that are more than one value.
 *
 * Kept out of the page so the wording is tested without rendering (pages are excluded from
 * coverage). Labels match the Edit Event form's, so a marketer reads the same names on both.
 */
import type { Event, EventTargets } from '../client/EventCalendarClient';
import { NONE, formatRange } from './eventDates';

/** The three metrics, in the Edit Event form's order and under its names (minus "Target"). */
const METRICS: readonly [keyof EventTargets, string][] = [
  ['nominationTarget', 'Nomination'],
  ['registrationTarget', 'Registration'],
  ['attendanceTarget', 'Attendance'],
];

/** "Nomination 100 · Attendance 50" — the metrics that are set, or undefined if none is. */
export function targetSummary(t: EventTargets | undefined): string | undefined {
  if (!t) return undefined;
  const parts = METRICS.filter(([k]) => t[k] !== undefined && t[k] !== null).map(
    ([k, label]) => `${label} ${t[k]}`,
  );
  return parts.length ? parts.join(' · ') : undefined;
}

/**
 * One line per target the event carries: the overall one, or one per BU prefixed with the BU.
 * The service stores the two independently, but the edit form writes only one mode, so both
 * are shown if both happen to be set rather than silently picking one. Empty when none is set.
 */
export function targetLines(event: Event): string[] {
  const lines: string[] = [];
  const overall = targetSummary(event.overallTarget);
  if (overall) lines.push(overall);
  for (const bu of event.byBusinessUnitTargets ?? []) {
    const summary = targetSummary(bu.targets);
    if (bu.businessUnit && summary) lines.push(`${bu.businessUnit}: ${summary}`);
  }
  return lines;
}

/** Yes / No — an unset flag is No, as the edit form reads it. */
export function nominationRequiredLabel(event: Event): string {
  return event.bdNominationSupported ? 'Yes' : 'No';
}

/** The nomination window, or a dash when the event takes no nominations. */
export function nominationWindow(event: Event): string {
  if (!event.bdNominationSupported) return NONE;
  return formatRange(event.nominationStartDate, event.nominationEndDate);
}
